import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import {
  MagnifyingGlassIcon,
  PlusIcon,
  StarIcon,
  BuildingOffice2Icon,
  BookmarkIcon,
  TrashIcon,
  TableCellsIcon,
} from '@heroicons/react/24/outline';
import { Card, CardContent } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { CanView } from '@/components/auth/CanView';
import { cn } from '@/utils/cn';
import {
  securityVendorService,
  type ListVendorsParams,
  type VendorFilters,
  type SecurityVendor,
  type SecurityVendorStatus,
  type SecurityVendorTier,
  type SecurityDomain,
  type ServiceRegion,
  type ComplianceCertification,
  type VendorContractStatus,
} from '@/services/securityVendor.service';
import VendorFormModal from '@/components/security-vendors/VendorFormModal';
import { MultiSelectDropdown } from '@/components/security-vendors/MultiSelectDropdown';
import {
  DOMAIN_LABELS,
  REGION_LABELS,
  STATUS_LABELS,
  STATUS_PILL,
  CERTIFICATION_LABELS,
  TIER_LABELS,
  CONTRACT_STATUS_LABELS,
  STATUS_VALUES,
  DOMAIN_VALUES,
  REGION_VALUES,
  CERTIFICATION_VALUES,
  TIER_VALUES,
  CONTRACT_STATUS_VALUES,
  SORT_OPTIONS,
} from '@/components/security-vendors/vendorOptions';

const PAGE_SIZE = 12;
const DEFAULT_SORT = 'updatedAt:desc';

const opts = <T extends string>(values: readonly T[], labels: Record<T, string>) =>
  values.map((v) => ({ value: v, label: labels[v] }));

function VendorCard({ vendor }: { vendor: SecurityVendor }) {
  const topDomains = vendor.domains.slice(0, 3);
  return (
    <Link to={`/security/vendor-directory/${vendor.id}`} className="block h-full">
      <Card hover="default" className="h-full cursor-pointer transition-all duration-200">
        <CardContent className="flex h-full flex-col p-5">
          <div className="mb-3 flex items-start gap-3">
            {vendor.logoUrl ? (
              <img
                src={vendor.logoUrl}
                alt=""
                className="h-10 w-10 flex-shrink-0 rounded-md object-contain"
                onError={(e) => { (e.currentTarget as HTMLImageElement).style.display = 'none'; }}
              />
            ) : (
              <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-md bg-secondary-100 dark:bg-secondary-800">
                <BuildingOffice2Icon className="h-5 w-5 text-secondary-400" />
              </div>
            )}
            <div className="min-w-0 flex-1">
              <h3 className="truncate font-semibold text-secondary-900 dark:text-white">{vendor.name}</h3>
              <span className={cn('mt-1 inline-block rounded-full px-2 py-0.5 text-xs font-medium', STATUS_PILL[vendor.status])}>
                {STATUS_LABELS[vendor.status]}
              </span>
            </div>
            <div className="flex flex-shrink-0 items-center gap-1 text-sm">
              <StarIcon className="h-4 w-4 text-amber-400" />
              <span className="font-medium text-secondary-700 dark:text-secondary-200">
                {vendor.compositeRating > 0 ? vendor.compositeRating.toFixed(1) : '—'}
              </span>
              <span className="text-xs text-secondary-400">({vendor.reviewCount})</span>
            </div>
          </div>

          {vendor.description && (
            <p className="mb-3 line-clamp-2 text-sm text-secondary-500 dark:text-secondary-400">{vendor.description}</p>
          )}

          {topDomains.length > 0 && (
            <div className="mb-3 flex flex-wrap gap-1.5">
              {topDomains.map((d) => (
                <span key={d} className="rounded bg-primary-50 px-2 py-0.5 text-xs text-primary-700 dark:bg-primary-900/30 dark:text-primary-300">
                  {DOMAIN_LABELS[d]}
                </span>
              ))}
              {vendor.domains.length > 3 && (
                <span className="rounded bg-secondary-100 px-2 py-0.5 text-xs text-secondary-500 dark:bg-secondary-800">
                  +{vendor.domains.length - 3}
                </span>
              )}
            </div>
          )}

          {vendor.regions.length > 0 && (
            <div className="mt-auto flex flex-wrap gap-1.5 pt-2">
              {vendor.regions.map((r) => (
                <span key={r} className="rounded-full border border-secondary-200 px-2 py-0.5 text-xs text-secondary-500 dark:border-secondary-700">
                  {REGION_LABELS[r]}
                </span>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </Link>
  );
}

export default function SecurityVendorDirectory() {
  const queryClient = useQueryClient();
  const [search, setSearch] = useState('');
  const [statuses, setStatuses] = useState<SecurityVendorStatus[]>([]);
  const [domains, setDomains] = useState<SecurityDomain[]>([]);
  const [regions, setRegions] = useState<ServiceRegion[]>([]);
  const [certifications, setCertifications] = useState<ComplianceCertification[]>([]);
  const [tiers, setTiers] = useState<SecurityVendorTier[]>([]);
  const [contractStatuses, setContractStatuses] = useState<VendorContractStatus[]>([]);
  const [country, setCountry] = useState('');
  const [minRating, setMinRating] = useState('');
  const [sort, setSort] = useState(DEFAULT_SORT);
  const [page, setPage] = useState(1);
  const [showAdd, setShowAdd] = useState(false);
  const [presetName, setPresetName] = useState('');

  // Any state change that narrows results returns to page 1 so the user never lands
  // on an out-of-range page.
  const onFilterChange = <T,>(setter: (v: T) => void) => (v: T) => { setter(v); setPage(1); };

  const filters = useMemo<VendorFilters>(() => {
    const sortOption = SORT_OPTIONS.find((o) => o.value === sort) ?? SORT_OPTIONS[0];
    return {
      search: search || undefined,
      status: statuses.length ? statuses : undefined,
      domains: domains.length ? domains : undefined,
      regions: regions.length ? regions : undefined,
      certifications: certifications.length ? certifications : undefined,
      vendorTier: tiers.length ? tiers : undefined,
      contractStatus: contractStatuses.length ? contractStatuses : undefined,
      country: country || undefined,
      minRating: minRating ? Number(minRating) : undefined,
      sortBy: sortOption.sortBy,
      sortOrder: sortOption.sortOrder,
    };
  }, [search, statuses, domains, regions, certifications, tiers, contractStatuses, country, minRating, sort]);

  const params = useMemo<ListVendorsParams>(
    () => ({ ...filters, page, limit: PAGE_SIZE }),
    [filters, page]
  );

  const { data, isLoading, isError, error, refetch } = useQuery({
    queryKey: ['security-vendors', params],
    queryFn: () => securityVendorService.list(params),
  });

  const { data: presets = [] } = useQuery({
    queryKey: ['vendor-filter-presets'],
    queryFn: () => securityVendorService.listPresets(),
  });

  const savePreset = useMutation({
    mutationFn: (name: string) => securityVendorService.createPreset(name, filters),
    onSuccess: () => {
      setPresetName('');
      toast.success('Filter preset saved');
      void queryClient.invalidateQueries({ queryKey: ['vendor-filter-presets'] });
    },
    onError: (e) => toast.error(e instanceof Error ? e.message : 'Failed to save preset'),
  });

  const deletePreset = useMutation({
    mutationFn: (id: string) => securityVendorService.deletePreset(id),
    onSuccess: () => {
      toast.success('Filter preset deleted');
      void queryClient.invalidateQueries({ queryKey: ['vendor-filter-presets'] });
    },
    onError: (e) => toast.error(e instanceof Error ? e.message : 'Failed to delete preset'),
  });

  const applyPreset = (id: string) => {
    const preset = presets.find((p) => p.id === id);
    if (!preset) {return;}
    const f = preset.filters;
    setSearch(f.search ?? '');
    setStatuses(f.status ?? []);
    setDomains(f.domains ?? []);
    setRegions(f.regions ?? []);
    setCertifications(f.certifications ?? []);
    setTiers(f.vendorTier ?? []);
    setContractStatuses(f.contractStatus ?? []);
    setCountry(f.country ?? '');
    setMinRating(f.minRating !== undefined && f.minRating !== null ? String(f.minRating) : '');
    setSort(f.sortBy ? `${f.sortBy}:${f.sortOrder ?? 'desc'}` : DEFAULT_SORT);
    setPage(1);
  };

  const clearFilters = () => {
    setSearch('');
    setStatuses([]);
    setDomains([]);
    setRegions([]);
    setCertifications([]);
    setTiers([]);
    setContractStatuses([]);
    setCountry('');
    setMinRating('');
    setSort(DEFAULT_SORT);
    setPage(1);
  };

  const vendors = data?.vendors ?? [];
  const pagination = data?.pagination;
  const totalPages = pagination?.totalPages ?? 1;

  const selectClass =
    'rounded-lg border border-secondary-300 bg-white px-3 py-2 text-sm dark:border-secondary-600 dark:bg-secondary-800 dark:text-white';

  return (
    <div className="space-y-6 p-4 sm:p-6">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-secondary-900 dark:text-white sm:text-3xl">Vendor Directory</h1>
          <p className="mt-1 text-secondary-500 dark:text-secondary-400">
            Recommended security vendors — ratings, coverage, and contacts.
          </p>
        </div>
        <div className="flex gap-2">
          <Link to="/security/vendor-directory/coverage-matrix">
            <Button variant="secondary" leftIcon={<TableCellsIcon className="h-4 w-4" />}>
              Coverage Matrix
            </Button>
          </Link>
          <CanView permission="security_vendors:create">
            <Button leftIcon={<PlusIcon className="h-4 w-4" />} onClick={() => setShowAdd(true)}>
              Add Vendor
            </Button>
          </CanView>
        </div>
      </div>

      <Card>
        <CardContent className="space-y-3 p-4">
          {/* Search + sort */}
          <div className="flex flex-col gap-3 lg:flex-row lg:items-center">
            <div className="flex-1">
              <Input
                placeholder="Search name, description, domain, region…"
                value={search}
                onChange={(e) => onFilterChange(setSearch)(e.target.value)}
                leftIcon={<MagnifyingGlassIcon className="h-4 w-4" />}
              />
            </div>
            <label className="flex items-center gap-2 text-sm text-secondary-500">
              Sort
              <select aria-label="Sort" value={sort} onChange={(e) => onFilterChange(setSort)(e.target.value)} className={selectClass}>
                {SORT_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
            </label>
          </div>

          {/* Multi-select filters */}
          <div className="grid grid-cols-2 gap-2 md:grid-cols-3 xl:grid-cols-6">
            <MultiSelectDropdown label="Domains" options={opts(DOMAIN_VALUES, DOMAIN_LABELS)} selected={domains} onChange={onFilterChange(setDomains)} />
            <MultiSelectDropdown label="Regions" options={opts(REGION_VALUES, REGION_LABELS)} selected={regions} onChange={onFilterChange(setRegions)} />
            <MultiSelectDropdown label="Certifications" options={opts(CERTIFICATION_VALUES, CERTIFICATION_LABELS)} selected={certifications} onChange={onFilterChange(setCertifications)} />
            <MultiSelectDropdown label="Contract status" options={opts(CONTRACT_STATUS_VALUES, CONTRACT_STATUS_LABELS)} selected={contractStatuses} onChange={onFilterChange(setContractStatuses)} />
            <MultiSelectDropdown label="Tier" options={opts(TIER_VALUES, TIER_LABELS)} selected={tiers} onChange={onFilterChange(setTiers)} />
            <MultiSelectDropdown label="Status" options={opts(STATUS_VALUES, STATUS_LABELS)} selected={statuses} onChange={onFilterChange(setStatuses)} />
          </div>

          {/* Country + rating + clear */}
          <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
            <Input
              placeholder="Country (exact match)"
              value={country}
              onChange={(e) => onFilterChange(setCountry)(e.target.value)}
              className="sm:max-w-xs"
            />
            <select aria-label="Minimum rating" value={minRating} onChange={(e) => onFilterChange(setMinRating)(e.target.value)} className={selectClass}>
              <option value="">Any rating</option>
              <option value="3">3+ stars</option>
              <option value="4">4+ stars</option>
              <option value="4.5">4.5+ stars</option>
            </select>
            <Button variant="ghost" size="sm" onClick={clearFilters}>Clear filters</Button>
          </div>

          {/* Saved presets (VIS-201) */}
          <div className="flex flex-col gap-2 border-t border-secondary-100 pt-3 dark:border-secondary-700 sm:flex-row sm:items-center">
            <BookmarkIcon className="hidden h-4 w-4 flex-shrink-0 text-secondary-400 sm:block" />
            <select
              aria-label="Load saved filter preset"
              value=""
              onChange={(e) => { if (e.target.value) {applyPreset(e.target.value);} }}
              className={selectClass}
            >
              <option value="">Load preset…</option>
              {presets.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
            </select>
            <Input
              placeholder="Save current filters as…"
              value={presetName}
              onChange={(e) => setPresetName(e.target.value)}
              className="sm:max-w-xs"
            />
            <Button
              size="sm"
              variant="outline"
              disabled={!presetName.trim() || savePreset.isPending}
              onClick={() => savePreset.mutate(presetName.trim())}
            >
              Save preset
            </Button>
            {presets.length > 0 && (
              <div className="flex flex-wrap items-center gap-1 text-xs text-secondary-500">
                {presets.map((p) => (
                  <span key={p.id} className="inline-flex items-center gap-1 rounded-full bg-secondary-100 px-2 py-0.5 dark:bg-secondary-700">
                    <button type="button" className="hover:underline" onClick={() => applyPreset(p.id)}>{p.name}</button>
                    <button
                      type="button"
                      aria-label={`Delete preset ${p.name}`}
                      onClick={() => deletePreset.mutate(p.id)}
                      className="text-secondary-400 hover:text-danger-600"
                    >
                      <TrashIcon className="h-3 w-3" />
                    </button>
                  </span>
                ))}
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {isLoading ? (
        <Card><CardContent className="p-12 text-center text-secondary-500">Loading vendors…</CardContent></Card>
      ) : isError ? (
        <Card>
          <CardContent className="p-12 text-center">
            <p className="mb-3 text-danger-600">{error instanceof Error ? error.message : 'Failed to load vendors'}</p>
            <Button variant="outline" onClick={() => void refetch()}>Retry</Button>
          </CardContent>
        </Card>
      ) : vendors.length === 0 ? (
        <Card><CardContent className="p-12 text-center text-secondary-500">No vendors found.</CardContent></Card>
      ) : (
        <>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-3">
            {vendors.map((v) => <VendorCard key={v.id} vendor={v} />)}
          </div>

          {totalPages > 1 && (
            <div className="flex items-center justify-between pt-2">
              <span className="text-sm text-secondary-500">
                {vendors.length} of {pagination?.total ?? 0}
              </span>
              <div className="flex items-center gap-2">
                <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage((p) => Math.max(1, p - 1))}>
                  Previous
                </Button>
                <span className="text-sm text-secondary-500">Page {page} of {totalPages}</span>
                <Button variant="outline" size="sm" disabled={page >= totalPages} onClick={() => setPage((p) => Math.min(totalPages, p + 1))}>
                  Next
                </Button>
              </div>
            </div>
          )}
        </>
      )}

      {showAdd && (
        <VendorFormModal
          onClose={() => setShowAdd(false)}
          onSaved={() => { setShowAdd(false); void refetch(); }}
        />
      )}
    </div>
  );
}
